import { Loader } from '../../components/Loader';
import { NumberRegistration } from '../../components/NumberAcquisition';
import { NumberRegistrationComponent } from '../../components/Registrations';
import { RecordVM } from '../../components/RecordVM';
import { GeneralSettings } from '../../components/RegularSettings/GeneralSettings';
import { UserGroupSettings } from '../../components/UserGroups';
import { UserManagement } from '../../components/UserManagement/UserManagement';
import { Typography } from '../../interfaces/typography';
import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { RootState } from '../../store';
import { CustomScores } from '../../components/CustomScores';
import { useUserData } from '../../lib/redux/users/user-data-hook';
import { SnippetManagement } from 'components/SnippetManagement';
import { VoiceNoteManagement } from 'components/VoiceNoteManagement';
import { Invoices } from '../../components/Invoice/Invoices';
import { AutodialerSettingsV2 } from 'components/AutodialerSettings/AutodialerSettings';
import { RequiresAdminAccess, RequiresParallelAccess } from '../../utils/CommonUIComponents';
import { PriorityOrderManagement } from '../../components/PriorityOrderManagement';
import { Integrations } from '../../components/Integrations';

export enum SettingsPages {
    GENERAL = 'General',
    AUTODIALER_SETTINGS = 'Dialer',
    CUSTOM_SCORES = 'AI Scoring',
    USER_MANAGEMENT = 'Users',
    SUBTEAMS = 'Subteams',
    NUMBERS = 'Numbers',
    REGISTRATIONS = 'Registrations',
    VOICEMAILS = 'Voicemail Drop',
    PRIORITY_ORDER = 'Inbound Routing',
    SNIPPETS = 'LinkedIn Snippets',
    VOICE_NOTES = 'LinkedIn Voice Notes',
    INVOICES = 'Billing',
    INTEGRATIONS = 'Integrations',
}

export type SettingsProps = {
    defaultSettings?: SettingsPages
}

export function Settings({ defaultSettings }: SettingsProps) {
  const { userLoaded } = useUserData()
  const adminLoaded = useSelector((state: RootState) => state.adminWrite.value !== null)
  
  const [activeSettingPage, setActiveTab] = useState<SettingsPages>(defaultSettings ?? SettingsPages.GENERAL)
  const navigate = useNavigate()
  const location = useLocation()

  const getSettingOptions = () => {
    // Show all options - access control is handled by the wrapper components
    const options = [
      SettingsPages.GENERAL, 
      SettingsPages.INTEGRATIONS,
      SettingsPages.AUTODIALER_SETTINGS,
      SettingsPages.USER_MANAGEMENT,
      SettingsPages.SUBTEAMS,
      SettingsPages.NUMBERS,
      SettingsPages.REGISTRATIONS,
      SettingsPages.PRIORITY_ORDER,
      SettingsPages.CUSTOM_SCORES,
      SettingsPages.SNIPPETS,
      SettingsPages.VOICE_NOTES,
      SettingsPages.VOICEMAILS,
      SettingsPages.INVOICES
    ]
    return options
  }

  const renderContent = () => {
    switch (activeSettingPage) {
      case SettingsPages.GENERAL:
        return <GeneralSettings />
      case SettingsPages.AUTODIALER_SETTINGS:
        return <AutodialerSettingsV2 />
      case SettingsPages.CUSTOM_SCORES:
        return (
          <RequiresAdminAccess>
            <CustomScores />
          </RequiresAdminAccess>
        )
      case SettingsPages.USER_MANAGEMENT:
        return (
          <RequiresAdminAccess>
            <UserManagement />
          </RequiresAdminAccess>
        )
      case SettingsPages.SUBTEAMS:
        return (
          <RequiresAdminAccess>
            <UserGroupSettings />
          </RequiresAdminAccess>
        )
      case SettingsPages.PRIORITY_ORDER:
        return (
          <RequiresAdminAccess>
            <RequiresParallelAccess>
              <PriorityOrderManagement />
            </RequiresParallelAccess>
          </RequiresAdminAccess>
        )
      case SettingsPages.NUMBERS:
        return (
          <RequiresParallelAccess>
            <NumberRegistration />
          </RequiresParallelAccess>
        )
      case SettingsPages.REGISTRATIONS:
        return (
          <RequiresAdminAccess>
            <RequiresParallelAccess>
              <NumberRegistrationComponent />
            </RequiresParallelAccess>
          </RequiresAdminAccess>
        )
      case SettingsPages.VOICEMAILS:
        return <RecordVM />
      case SettingsPages.SNIPPETS:
        return <SnippetManagement />
      case SettingsPages.VOICE_NOTES:
        return <VoiceNoteManagement />
      case SettingsPages.INVOICES:
        return <Invoices />
      case SettingsPages.INTEGRATIONS:
        return <Integrations />
      default:
        return (
          <div className="p-6">
            <Typography variant="h3" color="#374151">
              Select a setting option to configure
            </Typography>
          </div>
        )
    }
  }

  const updateSelectedSettings = (id: SettingsPages) => {
    if (!id) return
    if (id === activeSettingPage) return
    
    // Only check for unsaved changes when navigating between tabs
    const customScoresElement = document.querySelector('[role="tabpanel"]')
    if (activeSettingPage === SettingsPages.CUSTOM_SCORES && customScoresElement) {
      const hasChanges = customScoresElement.getAttribute('data-has-changes') === 'true'
      if (hasChanges) {
        const shouldLeave = window.confirm('You have unsaved changes. Are you sure you want to leave?')
        if (!shouldLeave) {
          return
        }
      }
    }
    
    navigate(`/settings?tab=${id}`)
  }

  const getCurrentId = useCallback(() => { 
    return new URLSearchParams(location.search).get("tab") 
  }, [location])
  
  useEffect(() => {
    const pageSessionId = getCurrentId()
    if (!pageSessionId) return
    if (!Object.values(SettingsPages).includes(pageSessionId as SettingsPages)) return
    // Don't show confirmation when URL changes externally
    setActiveTab(pageSessionId as SettingsPages)
  }, [location, getCurrentId])

  const renderOption = (settingPage: SettingsPages) => {
    return (
      <button
        key={settingPage}
        onClick={() => updateSelectedSettings(settingPage)}
        className={`
          w-full px-2 py-2.5 text-left transition-colors duration-200 relative border-l-2
          ${
            activeSettingPage === settingPage
              ? "bg-emerald-50 border-emerald-500"
              : "hover:bg-gray-50 border-transparent"
          }
        `}
      >
        <Typography 
          variant="mediumParagraph" 
          color={activeSettingPage === settingPage ? "#059669" : "#374151"}
          fontWeight={activeSettingPage === settingPage ? 500 : 400}
        >
          {settingPage}
        </Typography>
      </button>
    )
  }

  if (!userLoaded || !adminLoaded) {
    return <Loader />
  }

  return (
    <div className="h-full w-full flex bg-gray-50">
      {/* Sidebar */}
      <div className="w-36 bg-white border-r border-gray-200 flex-shrink-0 shadow-sm">
        <div className="px-2 py-4 border-b border-gray-200">
          <Typography variant="h4" color="#1F2937" fontWeight={600}>
            Settings
          </Typography>
        </div>
        <nav className="flex-1 overflow-y-auto">
          <div className="py-2">
            {getSettingOptions().map((settingPage) => renderOption(settingPage))}
          </div>
        </nav>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-hidden">
        <div className="h-full overflow-auto">
          {renderContent()}
        </div>
      </div>
    </div>
  )
}